'use client';

import type { Member } from '@/data/types';

interface DossierViewProps {
  member: Member;
  allMembers: Record<string, Member>;
  onSelectMember: (id: string) => void;
  onShowToast: (message: string) => void;
}

function getChurnColor(value: number): string {
  if (value > 60) return 'red';
  if (value > 35) return 'amber';
  if (value > 20) return 'gold';
  return 'green';
}

function getRenewalColor(value: number): string {
  if (value > 70) return 'green';
  if (value > 50) return 'gold';
  if (value > 30) return 'amber';
  return 'red';
}

function getEngagementColor(value: number): string {
  if (value > 65) return 'green';
  if (value > 45) return 'gold';
  if (value > 25) return 'amber';
  return 'red';
}

export default function DossierView({
  member,
  allMembers,
  onSelectMember,
  onShowToast,
}: DossierViewProps) {
  const m = member;

  // Sparse member -- show minimal dossier
  if (!m.callBrief) {
    return (
      <div className="dossier fade-enter">
        <div className="dossier-header">
          <div className="dossier-identity">
            <h1 className="dossier-name">{m.name}</h1>
            <p className="dossier-tagline">{m.tagline || m.industry}</p>
            <div className="dossier-vitals">
              <span className="dossier-vital">
                <strong>{m.age}</strong> years old
              </span>
              <span className="dossier-vital">
                <strong>{m.location}</strong>
              </span>
              <span className="dossier-vital">
                <strong>{m.company}</strong>
              </span>
              <span className="dossier-vital">
                <strong>{m.revenue}</strong> revenue
              </span>
            </div>
          </div>
        </div>
        <div className="dossier-section full-width" style={{ maxWidth: '100%' }}>
          <p className="narrative">
            Full profile data for this member has not been loaded into this demo.
            In the live platform, every member would have a complete dossier with
            business context, personal notes, engagement tracking, and suggested
            actions.
          </p>
        </div>
      </div>
    );
  }

  const churnColor = getChurnColor(m.churnRisk);
  const renewalColor = getRenewalColor(m.renewalReadiness);
  const engColor = getEngagementColor(m.engagementScore);
  const firstName = m.name.split(' ')[0];

  return (
    <div className="dossier fade-enter">
      {/* HEADER */}
      <div className="dossier-header">
        <div className="dossier-identity">
          <h1 className="dossier-name">{m.name}</h1>
          <p className="dossier-tagline">{m.tagline}</p>
          <div className="dossier-vitals">
            <span className="dossier-vital">
              <strong>{m.age}</strong> years old
            </span>
            <span className="dossier-vital">
              <strong>{m.location}</strong>
            </span>
            <span className="dossier-vital">
              Member since <strong>{m.memberSince}</strong>
            </span>
            <span className="dossier-vital">
              Renewal <strong>{m.nextRenewal}</strong>
            </span>
            {m.cohort && (
              <span className="dossier-vital">{m.cohort}</span>
            )}
          </div>
        </div>
        <div className="dossier-actions">
          <button
            className="dossier-action-btn primary"
            onClick={() => onShowToast(`Draft message opened for ${m.name}`)}
          >
            Draft Message
          </button>
          <button
            className="dossier-action-btn"
            onClick={() => onShowToast('Call notes view coming soon')}
          >
            View Call Notes
          </button>
          <button
            className="dossier-action-btn"
            onClick={() => onShowToast('Full timeline coming soon')}
          >
            Activity Timeline
          </button>
        </div>
      </div>

      {/* CALL BRIEF */}
      <div className="call-brief">
        <div className="call-brief-label">Before your call with {firstName}</div>
        <ul className="call-brief-points">
          {m.callBrief.map((point, i) => (
            <li key={i}>{point}</li>
          ))}
        </ul>
      </div>

      {/* GRID */}
      <div className="dossier-grid">
        {/* Business Snapshot */}
        <div className="dossier-section">
          <div className="section-label">Business Snapshot</div>
          <div className="kv-row">
            <span className="kv-key">Company</span>
            <span className="kv-val">{m.company}</span>
          </div>
          {m.otherVentures && (
            <div className="kv-row">
              <span className="kv-key">Also</span>
              <span className="kv-val" style={{ fontSize: '0.78rem' }}>
                {m.otherVentures}
              </span>
            </div>
          )}
          <div className="kv-row">
            <span className="kv-key">Industry</span>
            <span className="kv-val">{m.industry}</span>
          </div>
          <div className="kv-row">
            <span className="kv-key">Revenue</span>
            <span className="kv-val">{m.revenue}</span>
          </div>
          <div className="kv-row">
            <span className="kv-key">Team</span>
            <span className="kv-val">{m.employees}</span>
          </div>
          <div className="kv-row">
            <span className="kv-key">Stage</span>
            <span className="kv-val">{m.stage}</span>
          </div>
        </div>

        {/* Personal Context */}
        <div className="dossier-section">
          <div className="section-label">Personal Context</div>
          <div className="kv-row">
            <span className="kv-key">From</span>
            <span className="kv-val" style={{ fontSize: '0.78rem' }}>
              {m.originallyFrom}
            </span>
          </div>
          <div className="kv-row">
            <span className="kv-key">Family</span>
            <span className="kv-val">{m.family}</span>
          </div>
          <div style={{ marginTop: 12 }}>
            <div className="section-label" style={{ marginBottom: 8 }}>
              Personality
            </div>
            <p className="narrative" style={{ fontSize: '0.82rem' }}>
              {m.personality}
            </p>
          </div>
        </div>

        {/* Goals */}
        <div className="dossier-section">
          <div className="section-label gold">Current Goals</div>
          {m.goals?.map((g, i) => (
            <div
              key={i}
              style={{
                padding: '5px 0',
                fontSize: '0.82rem',
                color: 'var(--cream-muted)',
                borderBottom: '1px solid var(--cream-whisper)',
              }}
            >
              {g}
            </div>
          ))}
        </div>

        {/* Challenges */}
        <div className="dossier-section">
          <div className="section-label" style={{ color: 'var(--amber)' }}>
            Current Challenges
          </div>
          {m.challenges?.map((c, i) => (
            <div
              key={i}
              style={{
                padding: '5px 0',
                fontSize: '0.82rem',
                color: 'var(--cream-muted)',
                borderBottom: '1px solid var(--cream-whisper)',
              }}
            >
              {c}
            </div>
          ))}
        </div>

        {/* Gives & Needs */}
        <div className="dossier-section">
          <div className="section-label">What {firstName} Gives</div>
          <div className="tag-row">
            {m.gives?.map((g, i) => (
              <span key={i} className="tag gives">
                {g}
              </span>
            ))}
          </div>
          <div className="section-label" style={{ marginTop: 16 }}>
            What {firstName} Needs
          </div>
          <div className="tag-row">
            {m.needs?.map((n, i) => (
              <span key={i} className="tag needs">
                {n}
              </span>
            ))}
          </div>
        </div>

        {/* Health Indicators */}
        <div className="dossier-section">
          <div className="section-label">Health Indicators</div>
          <div className="score-row">
            <span className="score-label">Churn Risk</span>
            <div className="score-bar-track">
              <div
                className={`score-bar-fill ${churnColor}`}
                style={{ width: `${m.churnRisk}%` }}
              />
            </div>
            <span className="score-value">{m.churnRisk}</span>
          </div>
          <div className="score-row">
            <span className="score-label">Renewal Readiness</span>
            <div className="score-bar-track">
              <div
                className={`score-bar-fill ${renewalColor}`}
                style={{ width: `${m.renewalReadiness}%` }}
              />
            </div>
            <span className="score-value">{m.renewalReadiness}</span>
          </div>
          <div className="score-row">
            <span className="score-label">Engagement</span>
            <div className="score-bar-track">
              <div
                className={`score-bar-fill ${engColor}`}
                style={{ width: `${m.engagementScore}%` }}
              />
            </div>
            <span className="score-value">{m.engagementScore}</span>
          </div>
          <div style={{ marginTop: 14 }}>
            <div className="section-label" style={{ marginBottom: 6 }}>
              Emotional Trajectory
            </div>
            <p className="narrative" style={{ fontSize: '0.78rem' }}>
              {m.emotionalTrajectory}
            </p>
          </div>
        </div>

        {/* Recent Activity */}
        <div className="dossier-section">
          <div className="section-label">Recent Activity</div>
          {m.recentActivity?.map((a, i) => (
            <div key={i} className="engagement-row">
              <span className="engagement-channel">{a.channel}</span>
              <span className="engagement-detail">{a.detail}</span>
              <span className="engagement-when">{a.when}</span>
            </div>
          ))}
        </div>

        {/* Connection Matches */}
        <div className="dossier-section">
          <div className="section-label">Best Connection Matches</div>
          {m.matches?.map((match, i) => {
            const matchMember = allMembers[match.id];
            if (!matchMember) return null;
            return (
              <div
                key={i}
                className="match-card"
                onClick={() => onSelectMember(match.id)}
              >
                <div className="match-avatar">{matchMember.initials}</div>
                <div className="match-info">
                  <div className="match-name">{matchMember.name}</div>
                  <div className="match-reason">{match.reason}</div>
                </div>
                <div className="match-score">92%</div>
              </div>
            );
          })}
        </div>

        {/* Suggested Next Actions */}
        <div className="dossier-section full-width">
          <div className="section-label gold">Suggested Next Actions</div>
          <div className="action-list">
            {m.suggestedActions?.map((a, i) => (
              <div key={i} className="action-card">
                <div className={`action-priority-marker ${a.priority}`} />
                <div className="action-content">
                  <div className="action-title">{a.title}</div>
                  <div className="action-desc">{a.desc}</div>
                </div>
                <button
                  className="action-execute"
                  onClick={(e) => {
                    e.stopPropagation();
                    onShowToast(`Action queued: ${a.title}`);
                  }}
                >
                  Execute
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
